<template>
  <div class="filter-presets">
    <h3 class="text-sm font-medium mb-2 text-muted-foreground">Quick Filters</h3>
    <div class="grid grid-cols-2 gap-2">
      <button 
        v-for="preset in availablePresets" 
        :key="preset.id"
        @click="applyPreset(preset.id)"
        class="flex flex-col items-start p-3 rounded-md border transition-colors text-left"
        :class="isPresetActive(preset as FilterPreset) 
          ? 'border-2 border-primary' 
          : 'border-border hover:bg-accent/50'"
      >
        <span class="font-medium text-sm" :class="isPresetActive(preset as FilterPreset) ? 'text-primary' : ''">{{ preset.name }}</span>
        <span class="text-xs text-muted-foreground">{{ preset.description }}</span>
      </button>
    </div>
    <div v-if="hasActivePreset" class="mt-2 text-center">
        <button @click="clearActivePresets" class="text-xs text-primary hover:underline">Clear quick filter</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import type { FilterPreset } from '../../../../types'

const vehiclesStore = useVehiclesStore()

// Generate presets dynamically based on actual inventory data
const generateDataDrivenPresets = (): FilterPreset[] => {
  const { availableFilters } = vehiclesStore
  const presets: FilterPreset[] = []

  // Family Cars - only if we have SUVs, wagons, or hatches with 5+ seats
  const hasFamilyVehicles = 
    availableFilters.body?.some((body: string) => ['suv', 'wagon', 'hatch'].includes(body.toLowerCase())) &&
    availableFilters.seats?.some((seats: string) => parseInt(seats) >= 5)
  
  if (hasFamilyVehicles) {
    presets.push({
      id: 'family_cars',
      name: 'Family Cars',
      description: 'SUVs, wagons & hatches with 5+ seats',
      filters: {
        body: availableFilters.body?.filter((body: string) => 
          ['suv', 'wagon', 'hatch'].includes(body.toLowerCase())
        ) || [],
        seats: availableFilters.seats?.filter((seats: string) => parseInt(seats) >= 5) || []
      }
    })
  }

  // Budget Cars ($20,000 and under) - as requested by user
  const hasBudgetVehicles = 
    availableFilters.priceRange && availableFilters.priceRange.min <= 20000
  
  if (hasBudgetVehicles) {
    presets.push({
      id: 'budget_cars',
      name: 'Budget Cars',
      description: '$20,000 and under',
      filters: {
        price: { min: 0, max: 20000 }
      }
    })
  }

  // Fuel Efficient - only if we have hybrid, electric, or efficient petrol vehicles
  const hasEfficientVehicles = 
    availableFilters.fuel?.some((fuel: string) => 
      ['hybrid', 'electric', 'petrol'].includes(fuel.toLowerCase())
    )
  
  if (hasEfficientVehicles) {
    presets.push({
      id: 'fuel_efficient',
      name: 'Fuel Efficient',
      description: 'Hybrid, electric & petrol vehicles',
      filters: {
        fuel: availableFilters.fuel?.filter((fuel: string) => 
          ['hybrid', 'electric', 'petrol'].includes(fuel.toLowerCase())
        ) || []
      }
    })
  }

  // Off-Road Ready - only if we have 4WD/AWD vehicles
  const hasOffRoadVehicles = 
    availableFilters.drivetrain?.some((drivetrain: string) => 
      ['4x4', '4x4-constant', '4x4-dual-range', '4x4-on-demand', 'four-wheel-drive', 'awd', '4wd'].includes(drivetrain.toLowerCase())
    ) && availableFilters.body?.some((body: string) => ['suv', 'ute'].includes(body.toLowerCase()))
  
  if (hasOffRoadVehicles) {
    presets.push({
      id: 'off_road',
      name: 'Off-Road Ready',
      description: '4WD/AWD SUVs & utes',
      filters: {
        drivetrain: availableFilters.drivetrain?.filter((drivetrain: string) => 
          ['4x4', '4x4-constant', '4x4-dual-range', '4x4-on-demand', 'four-wheel-drive', 'awd', '4wd'].includes(drivetrain.toLowerCase())
        ) || [],
        body: availableFilters.body?.filter((body: string) => ['suv', 'ute'].includes(body.toLowerCase())) || []
      }
    })
  }

  // City Commuter - only if we have compact automatics
  const hasCityVehicles = 
    availableFilters.body?.some((body: string) => ['hatch', 'sedan'].includes(body.toLowerCase())) &&
    availableFilters.transmission?.some((trans: string) => trans.toLowerCase().includes('automatic'))
  
  if (hasCityVehicles) {
    presets.push({
      id: 'city_commuter',
      name: 'City Commuter',
      description: 'Compact automatic vehicles',
      filters: {
        body: availableFilters.body?.filter((body: string) => ['hatch', 'sedan'].includes(body.toLowerCase())) || [],
        transmission: availableFilters.transmission?.filter((trans: string) => 
          trans.toLowerCase().includes('automatic')
        ) || []
      }
    })
  }

  // Luxury Vehicles - only if we have vehicles over $50,000
  const hasLuxuryVehicles = 
    availableFilters.priceRange && availableFilters.priceRange.max >= 50000
  
  if (hasLuxuryVehicles) {
    presets.push({
      id: 'luxury',
      name: 'Luxury Vehicles',
      description: 'Premium vehicles $50k+',
      filters: {
        price: { min: 50000, max: availableFilters.priceRange.max }
      }
    })
  }

  return presets
}

// Helper function to check if a vehicle matches a set of preset filters
const vehicleMatchesPresetFilters = (vehicle: any, presetFilters: Record<string, any>): boolean => {
  if (Object.keys(presetFilters).length === 0) {
    return true
  }

  for (const [filterName, presetValue] of Object.entries(presetFilters)) {
    const vehicleValue = vehicle[filterName]

    if (vehicleValue === undefined || vehicleValue === null) {
      if (presetValue !== null && presetValue !== undefined && !(Array.isArray(presetValue) && presetValue.length === 0)) {
        return false
      }
      continue
    }

    if (Array.isArray(presetValue)) {
      if (presetValue.length === 0) {
        continue
      }
      // Handle vehicle attributes that have {value: []} structure
      const vehicleActualValue = String(vehicleValue?.value?.[0] || vehicleValue).toLowerCase()
      const presetValuesLower = presetValue.map(pv => String(pv).toLowerCase())

      if (!presetValuesLower.includes(vehicleActualValue)) {
        return false
      }
    } else if (typeof presetValue === 'object' && presetValue !== null && 'min' in presetValue && 'max' in presetValue) {
      // Handle price range filters
      const numVehicleValue = Number(vehicleValue)
      if (isNaN(numVehicleValue) || numVehicleValue < presetValue.min || numVehicleValue > presetValue.max) {
        return false
      }
    } else {
      // Direct comparison for single string values
      const vehicleActualValue = String(vehicleValue?.value?.[0] || vehicleValue).toLowerCase()
      const presetActualValue = String(presetValue).toLowerCase()
      if (vehicleActualValue !== presetActualValue) {
        return false
      }
    }
  }
  return true
}

// Helper function to determine if a preset would yield any results from the current vehicle list
const wouldPresetYieldResults = (preset: FilterPreset, vehicles: any[]): boolean => {
  if (!vehicles || vehicles.length === 0) {
    return false
  }
  if (!preset.filters || Object.keys(preset.filters).length === 0) {
    return true
  }

  return vehicles.some(vehicle => vehicleMatchesPresetFilters(vehicle, preset.filters))
}

// availablePresets is now a computed property based on actual inventory data
const availablePresets = computed(() => {
  const allVehicles = vehiclesStore.vehicles
  
  if (!allVehicles || allVehicles.length === 0) {
    return []
  }

  // Generate presets based on actual inventory
  const dataPresets = generateDataDrivenPresets()
  
  // Filter to only show presets that would yield results
  const filtered = dataPresets.filter(preset => {
    const yieldsResults = wouldPresetYieldResults(preset, allVehicles)
    return yieldsResults
  })
  
  return filtered
})

// Function to check if a preset is active
const isPresetActive = (preset: FilterPreset): boolean => {
  if (!preset?.filters) return false
  
  return Object.entries(preset.filters).every(([filterName, presetValue]) => {
    const activeValue = vehiclesStore.activeFilters[filterName]

    if (activeValue === undefined || activeValue === null) {
        return presetValue === null || presetValue === undefined || (Array.isArray(presetValue) && presetValue.length === 0)
    }

    if (Array.isArray(presetValue)) {
        let activeArray: string[] = Array.isArray(activeValue) ? activeValue : []
        if (presetValue.length !== activeArray.length) return false
        const presetSet = new Set(presetValue.map(v => String(v).toLowerCase()))
        const activeSet = new Set(activeArray.map(v => String(v).toLowerCase()))
        return presetValue.every(val => activeSet.has(String(val).toLowerCase()))
    } else if (typeof presetValue === 'object' && presetValue !== null && 'min' in presetValue && 'max' in presetValue) {
        if (!(typeof activeValue === 'object' && activeValue !== null && 'min' in activeValue && 'max' in activeValue)) return false
        return activeValue.min === presetValue.min && activeValue.max === presetValue.max
    } else {
         let currentActiveValue = activeValue
         if (typeof activeValue === 'object' && activeValue !== null && 'value' in activeValue) {
             currentActiveValue = activeValue.value
         }
         return String(currentActiveValue) === String(presetValue)
    }
  })
}

// Apply a preset by setting the activeFilters in the store
const applyPreset = (presetId: string) => {
  const preset = availablePresets.value.find((p: FilterPreset) => p.id === presetId)
  if (!preset) return
  
  // Call the main store's action to apply the filter set
  vehiclesStore.applyFilters(preset.filters)
  // URL update is handled by the watcher in index.vue
}

// Computed property to check if any preset is active
const hasActivePreset = computed(() => {
  return availablePresets.value.some((preset: FilterPreset) => isPresetActive(preset))
})

// Clear active presets by setting filters to empty
const clearActivePresets = () => {
  // Clear all filters to return to default state
  vehiclesStore.applyFilters({})
}
</script>

